import { SagaIterator } from 'redux-saga';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { CRASH_DIALOG } from 'common/components/crash-dialog';
import { SubscriptionType } from 'common/constants/subscription';
import { Role } from 'common/interfaces/account/role';
import { ActionWith } from 'common/interfaces/action-with';
import { State } from 'common/interfaces/state';
import { KreoDialogActions } from 'common/UIKit/dialogs/actions';
import { RedirectUrlUtils } from 'common/utils/redirect-url-utils';
import { CompanySubscriptionModel } from 'unit-account/interfaces/company-subscription-model';
import { SubscriptionRolesApi } from 'unit-subscription/api/subscription-roles';
import { SubscriptionStatus } from 'unit-subscription/enums/subscription-status';
import { AccountActions } from '../../../units/account/actions/creators';
import { ValidationField } from '../../../units/account/actions/payloads';
import { PersistedStorageActions } from '../../../units/persisted-storage/actions/creators';
import { SubscriptionActions } from '../actions/creators';
import {
  CreateSubscriptionEstimationPayload,
  FormWithSubscriptionId,
  SubscriptionAndCompanyIdPayload,
} from '../actions/payloads';
import { SubscriptionActionTypes } from '../actions/types';
import { SubscriptionApi } from '../api/subscription';
import { SUBSCRIBE_PAGE_APPROVE_DIALOG } from '../components/subscribe-page-approve-dialog';
import { CompanyBillingInfoModel } from '../interfaces/company-billing-info-model';
import { CreateSubscriptionForm } from '../interfaces/create-subscription-form';
import { EstimateSubscriptionForm } from '../interfaces/estimate-subscription-form';
import { SubscriptionPlanListingModel } from '../interfaces/subscription-plan-listing-model';
import { UpdateSubscriptionForm } from '../interfaces/update-subscription-form';


function* loadSubscriptionRoles(action: ActionWith<number>): SagaIterator {
  try {
    const roles: any[] = yield call(SubscriptionRolesApi.loadSubscriptionRoles);
    const data: Role[] = roles.map(x => ({ id: x.id, name: x.name, group: x.roleGroup }));
    yield put(SubscriptionActions.loadSubscriptionRolesSucceeded(data));
  } catch (error) {
    console.error('subscription: load subscription roles failed', error, action.payload);
  }
}


function* getSubscriptionPlans(action: ActionWith<SubscriptionType>): SagaIterator {
  try {
    const model: SubscriptionPlanListingModel = yield call(SubscriptionApi.getSubscriptionPlans, action.payload);
    model.plans = model.plans.sort((a, b) => a.order - b.order);
    yield put(SubscriptionActions.getSubscriptionPlansSucceeded(model));
  } catch (error) {
    console.error('subscription: get subscription plans failed', error);
    yield put(SubscriptionActions.getSubscriptionPlansFailed());
  }
}

function* createSubscription(action: ActionWith<CreateSubscriptionForm>): SagaIterator {
  try {
    const [companyId, subscriptionType]: [number, SubscriptionType] = yield select<State>(
      state => ([
        state.account.selectedCompany && state.account.selectedCompany.id,
        state.account.selectedSubscriptionType,
      ]));
    const companies = yield call(SubscriptionApi.createSubscription, companyId, action.payload);
    yield put(AccountActions.fetchCompaniesSuccess(companies));
    yield put(AccountActions.getCurrentUserInfo());
    yield put(KreoDialogActions.closeDialog(SUBSCRIBE_PAGE_APPROVE_DIALOG));
    RedirectUrlUtils.redirectTo(subscriptionType);
  } catch (error) {
    yield call(handleUpdateSubscriptionException, error);
    console.error('subscription: create subscription failed', error);
  }
}

function* updateSubscription({ payload }: ActionWith<FormWithSubscriptionId<UpdateSubscriptionForm>>): SagaIterator {
  try {
    yield call(SubscriptionApi.updateSubscription, payload.subscriptionId, payload.form);
    yield put(AccountActions.fetchCompanies());
    yield put(AccountActions.getCurrentUserInfo());
    // const subscriptionType = yield select<State>(state => state.account.selectedSubscriptionType);
    // RedirectUrlUtils.redirectTo(subscriptionType);
  } catch (error) {
    yield call(handleUpdateSubscriptionException, error);
    console.error('subscription: update subscription failed', error);

  }
}

function* cancelSubscription({ payload }: ActionWith<string>): SagaIterator {
  try {
    yield call(SubscriptionApi.cancelSubscription, payload);
    yield put(AccountActions.fetchCompanies());
  } catch (error) {
    console.error('subscription: cancel subscription failed', error);
  }
}

function* renewSubscription({ payload }: ActionWith<FormWithSubscriptionId<UpdateSubscriptionForm>>): SagaIterator {
  try {
    const subscriptionType = yield select<State>(state => state.account.selectedSubscriptionType);
    const subscription: CompanySubscriptionModel = yield call(
      SubscriptionApi.updateSubscription, payload.subscriptionId, payload.form);
    yield put(AccountActions.fetchCompanies());
    yield put(AccountActions.getCurrentUserInfo());
    if (subscription.status === SubscriptionStatus.Canceled) {
      yield call(SubscriptionApi.renewSubscription, payload.subscriptionId);
    }
    RedirectUrlUtils.redirectTo(subscriptionType);
    yield put(AccountActions.fetchCompanies());
  } catch (error) {
    yield call(handleUpdateSubscriptionException, error);
    console.error('subscription: renew subscription failed', error);
  }
}

function* getUpdateSubscriptionEstimation(
  { payload }: ActionWith<FormWithSubscriptionId<EstimateSubscriptionForm>>,
): SagaIterator {
  try {
    const { subscriptionId, form } = payload;
    const estimation = yield call(SubscriptionApi.getUpdateSubscriptionEstimation, subscriptionId, form);
    yield put(SubscriptionActions.setSubscriptionEstimation(estimation));

  } catch (error) {
    yield call(handleUpdateSubscriptionException, error);
    console.error('subscription: get update subscription estimation failed', error);
  }
}

function* getCreateSubscriptionEstimation(
  { payload }: ActionWith<CreateSubscriptionEstimationPayload>,
): SagaIterator {
  try {
    const { subscriptionType, form } = payload;
    const estimation = yield call(SubscriptionApi.getCreateSubscriptionEstimation, subscriptionType, form);
    yield put(SubscriptionActions.setSubscriptionEstimation(estimation));

  } catch (error) {
    console.error('subscription: get create subscription estimation failed', error);
  }
}

function* fetchOwnCompanyBillingInfo(action: ActionWith<SubscriptionType>): SagaIterator {
  try {
    const model: CompanyBillingInfoModel = yield call(SubscriptionApi.getBillingInfo, action.payload);
    yield put(SubscriptionActions.fetchOwnCompanyBillingInfoSucceeded(model));
  } catch (error) {
    yield put(SubscriptionActions.fetchOwnCompanyBillingInfoFailed());
    console.error('subscription: fetch own company billing info failed', error);
  }
}

function* setSubscriptionPaymentMethod(action: ActionWith<FormWithSubscriptionId<string>>): SagaIterator {
  try {
    const { subscriptionId, form: cardId } = action.payload;
    yield call(SubscriptionApi.setSubscriptionPaymentMethod, subscriptionId, cardId);
  } catch (error) {
    yield put(SubscriptionActions.fetchOwnCompanyBillingInfoFailed());
    console.error('subscription: fetch own company billing info failed', error);
  }
}

function getMessage(error: any): [string, boolean] {
  try {
    const responseError = JSON.parse(error.message);
    const message: string = responseError.response.data.message;
    return [message, true];
  } catch {
    return [error.message, false];
  }
}

function* handleUpdateSubscriptionException(error: any): SagaIterator {
  const [message, isJSON] = getMessage(error);
  if (isJSON) {
    yield put(KreoDialogActions.openDialog(CRASH_DIALOG, 'Sorry, something went wrong.'));
  } else if (/(customer\[(.+)\].+):(.+)/.test(message)) {
    const [, , field, errorText] = message.match(/(customer\[(.+)\].+):(.+)/);
    yield put(AccountActions.setSubscribeError(field.trim() as ValidationField, errorText.trim()));
  } else if (/coupons/.test(message)) {
    yield put(SubscriptionActions.setSubscriptionEstimationError(
      {
        message: 'Oops... The coupon is invalid',
        couponIndex: 0,
      }));
  } else {
    yield put(KreoDialogActions.openDialog(CRASH_DIALOG, message));
  }
  console.error('subscription: update subscription failed', error);
}

function* persistSelectedCompanyId({ payload }: ActionWith<SubscriptionAndCompanyIdPayload>): SagaIterator {
  try {
    yield put(PersistedStorageActions.setSelectedCompanyId(payload.companyId));
  } catch (error) {
    console.error('subscription: persist selected company id failed', error, { payload });
  }
}

export function* subscriptionSagas(): SagaIterator {
  yield takeLatest(SubscriptionActionTypes.LOAD_SUBSCRIPTION_ROLES, loadSubscriptionRoles);
  // yield takeLatest(
  //   SubscriptionActionTypes.UPDATE_SUBSCRIPTION_PERMISSION_ROLES_REQUEST,
  //   updateSubscriptionPermissionRoles,
  // );
  // yield takeLatest(SubscriptionActionTypes.UPDATE_SUBSCRIPTION_ROLES_REQUEST, updateSubscriptionRoles);
  yield takeLatest(SubscriptionActionTypes.UPDATE_SUBSCRIPTION, updateSubscription);
  yield takeLatest(SubscriptionActionTypes.CANCEL_SUBSCRIPTION, cancelSubscription);
  yield takeLatest(SubscriptionActionTypes.RENEW_SUBSCRIPTION, renewSubscription);
  yield takeLatest(SubscriptionActionTypes.GET_UPDATE_SUBSCRIPTION_ESTIMATION, getUpdateSubscriptionEstimation);
  yield takeLatest(SubscriptionActionTypes.GET_CREATE_SUBSCRIPTION_ESTIMATION, getCreateSubscriptionEstimation);
  yield takeLatest(SubscriptionActionTypes.GET_SUBSCRIPTION_PLANS_REQUEST, getSubscriptionPlans);
  yield takeLatest(SubscriptionActionTypes.CREATE_SUBSCRIPTION, createSubscription);
  yield takeLatest(SubscriptionActionTypes.FETCH_OWN_COMPANY_BILLING_INFO_REQUEST, fetchOwnCompanyBillingInfo);
  yield takeLatest(SubscriptionActionTypes.SET_SUBSCRIPTION_PAYMENT_METHOD_REQUEST, setSubscriptionPaymentMethod);
  yield takeLatest(SubscriptionActionTypes.SET_SUBSCRIPTION_TYPE_AND_COMPANY_ID, persistSelectedCompanyId);
}
